import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, firstValueFrom } from 'rxjs';
import * as signalR from "@microsoft/signalr";
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { environment } from 'src/environments/environment';
import { LoggingService } from './services/logging.service';
import { ActorSendGroupStatusInformationModel } from './models/api/actor-status-information.model';
import { EventLogModel } from './services/api-clients/event-log.api-client';
import { DashboardRequestModel } from './models/dashboard/dashboard-request.model';
import { StatusRequestModel } from './models/status/status-request.model';
import { BuilderRequestModel } from './models/builder/builder-request.model';
import { NoteModel } from './models/notes/note.model';
import { ToastService } from './services/toast.service';
// SignalR is required in the app component for real-time communication.

/**
 * Transfer model for notifying SignalR that a request has been deleted.
 */
export class DeleteRequestModel {
  requestGuid!: string
}

export class NotificationModel {
  requestGuid!: string;
  notificationType!: NotificationType;
}

export enum NotificationType {
  InternalServerError = "InternalServerError",
  DocumentDeleted = "DocumentDeleted"
}

@Injectable({
  providedIn: 'root'
})

export class PlatformHub {

  constructor(private oidcSecurityService: OidcSecurityService, private loggingService: LoggingService, private readonly toastService: ToastService) { }

  /**
   * The SignalR HubConnection instance. Null if not connected.
   */
  private platformHub: signalR.HubConnection | null = null;
  /**
   * Retry counter for connection attempts.
   */
  private retrys: number = 0;
  /**
   * Emits error or status events for PKI operations.
   */
  private subject: Subject<string[]> = new Subject<string[]>();
  /**
   * Emits true if the hub is started, false otherwise.
   */
  public hasStartedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /**
   * True if a connection attempt is in progress.
   */
  public starting: boolean = false;
  /**
   * Observable for PKI events (errors, status, etc).
   */
  public pkisEvent = this.subject.asObservable();

  /**
   * Emits the latest list of actors for a request. New subscribers immediately receive the latest value.
   */
  private actorsSubject: BehaviorSubject<ActorSendGroupStatusInformationModel[]> = new BehaviorSubject<ActorSendGroupStatusInformationModel[]>([]);
  public actors$ = this.actorsSubject.asObservable();

  /**
   * Emits event log updates for requests. Stateless event stream.
   */
  private eventLogSubject: Subject<EventLogModel> = new Subject<EventLogModel>();
  public eventLog$ = this.eventLogSubject.asObservable();

  /**
   * Emits when a new note is added to a request. Stateless event stream.
   */
  private newNoteSubject: Subject<NoteModel> = new Subject<NoteModel>();
  public newNote$ = this.newNoteSubject.asObservable();
  /**
   * Emits when a note is updated. Stateless event stream.
   */
  private updatedNoteSubject: Subject<NoteModel> = new Subject<NoteModel>();
  public updateNotes$ = this.updatedNoteSubject.asObservable();
  /**
   * Emits when a note is deleted. Stateless event stream.
   */
  private deletedNoteSubject: Subject<string> = new Subject<string>();
  public deletedNote$ = this.deletedNoteSubject.asObservable();

  /**
   * Emits when a dashboard request is updated. Stateless event stream.
   */
  private singularRequestUpdatedSubject: Subject<DashboardRequestModel> = new Subject<DashboardRequestModel>();
  public singularRequestUpdatedSubject$ = this.singularRequestUpdatedSubject.asObservable();

  /**
   * Emits when a builder request is updated. Stateless event stream.
   */
  private builderRequestUpdatedSubject: Subject<BuilderRequestModel> = new Subject<BuilderRequestModel>();
  public builderRequestUpdatedSubject$ = this.builderRequestUpdatedSubject.asObservable();

  /**
   * Emits when a status request is updated. Stateless event stream.
   */
  private statusRequestUpdatedSubject: Subject<StatusRequestModel> = new Subject<StatusRequestModel>();
  public statusRequestUpdatedSubject$ = this.statusRequestUpdatedSubject.asObservable();

  /**
   * Emits when a request should be removed from the dashboard. Stateless event stream.
   */
  private requestGuidToRemoveSubject: Subject<string> = new Subject<string>();
  public requestGuidToRemoveSubject$ = this.requestGuidToRemoveSubject.asObservable();

  /**
   * Tracks all request GUIDs currently subscribed to status updates. Used for automatic resubscribe after reconnect.
   * In practice, this is usually a small set (often just one or a few per user context).
   */
  private subscribedRequestStatusGuids: Set<string> = new Set();
  /**
   * Tracks all request GUIDs currently subscribed to event log updates. Used for automatic resubscribe after reconnect.
   */
  private subscribedRequestEventLogGuids: Set<string> = new Set();
  /**
   * Tracks all request GUIDs currently subscribed to notes updates. Used for automatic resubscribe after reconnect.
   */
  private subscribedRequestNotesGuids: Set<string> = new Set();

  /**
   * SignalR group name for request status updates.
   */
  public static RequestStatusGroup = "RequestStatus";
  /**
   * SignalR group name for request event log updates.
   */
  public static RequestEventLogGroup = "RequestEventlog";
  /**
   * SignalR group name for request notes updates.
   */
  public static RequestNotesGroup = "RequestNotes";




  /**
   * Starts the SignalR connection. Prevents duplicate connections, sets up all event handlers, and handles automatic resubscribe after reconnect.
   */
  public async start(): Promise<void> {
    // Prevent duplicate connections
    if (this.platformHub && (this.platformHub.state === signalR.HubConnectionState.Connected || this.platformHub.state === signalR.HubConnectionState.Connecting || this.starting)) {
      return;
    }
    this.starting = true;

    // Read Azure slot cookie if present (for deployment slot routing)
    let slot = document.cookie.split('; ').find(row => row?.startsWith("slot="))?.split('=')[1];
    if (slot)
      slot = "x-ms-routing-name=" + slot + "&";
    else
      slot = "";

    // Configure SignalR connection options, including access token factory for authentication
    const options: signalR.IHttpConnectionOptions = {
      transport: signalR.HttpTransportType.WebSockets,
      accessTokenFactory: async () => {
        const token = await firstValueFrom(this.oidcSecurityService.getAccessToken());
        return token || '';
      }
    };

    // Build the SignalR connection
    this.platformHub = new signalR.HubConnectionBuilder()
      .withUrl(environment.apiUrl + "/platformhub?" + slot, options)
      .withAutomaticReconnect([0, 1000, 5000, 10000, 60000])
      .build();
    this.platformHub.serverTimeoutInMilliseconds = 240000; // 4 minutes

    // Automatically resubscribe to all groups after reconnect
    this.platformHub.onreconnected(() => {
      this.resubscribeAll();
    });

    // Register all SignalR event handlers for real-time updates
    this.platformHub.on('ActorsUpdated', (actorList: ActorSendGroupStatusInformationModel[]) => {
      this.actorsSubject.next(actorList);
    });
    this.platformHub.on('EventLogExpanded', (eventLog: EventLogModel) => {
      this.eventLogSubject.next(eventLog);
    });
    this.platformHub.on('NoteAdded', (note: NoteModel) => {
      this.newNoteSubject.next(note);
    });
    this.platformHub.on('NoteUpdated', (note: NoteModel) => {
      this.updatedNoteSubject.next(note);
    });
    this.platformHub.on('DeletedNote', (noteGuid: string) => {
      this.deletedNoteSubject.next(noteGuid);
    });
    this.platformHub.on('RequestUpdated', (dashboardmodel: DashboardRequestModel) => {
      if (dashboardmodel.requestId) {
        this.singularRequestUpdatedSubject.next(dashboardmodel);
      }
    });
    this.platformHub.on('DossierDeleted', (dossier: DeleteRequestModel) => {
      if (dossier && dossier.requestGuid) {
        this.requestGuidToRemoveSubject.next(dossier.requestGuid);
      }
    });
    this.platformHub.on('NotificationAdded', (notification: NotificationModel) => {
      this.toastService.showNotification({
        ...notification,
        // Cast the notificationType to the enum for better type safety in the toast service
        notificationType: notification.notificationType as NotificationType
      });
    });
    this.platformHub.on('BuilderRequestUpdated', (builderModel: BuilderRequestModel) => {
      this.builderRequestUpdatedSubject.next(builderModel);
    });
    this.platformHub.on('StatusRequestUpdated', (statusModel: StatusRequestModel) => {
      this.statusRequestUpdatedSubject.next(statusModel);
    });

    // Start the connection and handle errors
    try {
      await this.platformHub.start();
      this.hasStartedSubject.next(true);
    } catch (e:any) {
      this.loggingService.logException(e);
      this.subject.next(['error', String(e)]);
      this.hasStartedSubject.next(false);
    } finally {
      this.starting = false;
    }
  }


  /**
   * Subscribes to status updates for a specific request and tracks the subscription for resubscribe after reconnect.
   * @param requestGuid The unique identifier of the request to subscribe to.
   */
  public subscribeToRequestStatusGroup(requestGuid: string) {
    if (this.platformHub) {
      this.platformHub.invoke("Subscribe_" + PlatformHub.RequestStatusGroup, requestGuid)
        .then(() => this.subscribedRequestStatusGuids.add(requestGuid))
        .catch(e => this.loggingService.logException(e));
    }
  }

  /**
   * Unsubscribes from status updates for a specific request and removes the subscription from the tracking set.
   * @param requestGuid The unique identifier of the request to unsubscribe from.
   */
  public unsubscribeToRequestStatusGroup(requestGuid: string) {
    if (this.platformHub) {
      this.platformHub.invoke("Unsubscribe_" + PlatformHub.RequestStatusGroup, requestGuid)
        .then(() => this.subscribedRequestStatusGuids.delete(requestGuid))
        .catch(e => this.loggingService.logException(e));
    }
  }

  /**
   * Subscribes to event log updates for a specific request and tracks the subscription for resubscribe after reconnect.
   * @param requestGuid The unique identifier of the request to subscribe to.
   */
  public subscribeToRequestEventLog(requestGuid: string) {
    if (this.platformHub) {
      this.platformHub.invoke("Subscribe_" + PlatformHub.RequestEventLogGroup, requestGuid)
        .then(() => this.subscribedRequestEventLogGuids.add(requestGuid))
        .catch(e => this.loggingService.logException(e));
    }
  }

  /**
   * Unsubscribes from event log updates for a specific request and removes the subscription from the tracking set.
   * @param requestGuid The unique identifier of the request to unsubscribe from.
   */
  public unsubscribeToRequestEventLog(requestGuid: string) {
    if (this.platformHub) {
      this.platformHub.invoke("Unsubscribe_" + PlatformHub.RequestEventLogGroup, requestGuid)
        .then(() => this.subscribedRequestEventLogGuids.delete(requestGuid))
        .catch(e => this.loggingService.logException(e));
    }
  }

  /**
   * Subscribes to notes updates for a specific request and tracks the subscription for resubscribe after reconnect.
   * @param requestGuid The unique identifier of the request to subscribe to.
   */
  public subscribeToRequestNotes(requestGuid: string) {
    if (this.platformHub) {
      this.platformHub.invoke("Subscribe_" + PlatformHub.RequestNotesGroup, requestGuid)
        .then(() => this.subscribedRequestNotesGuids.add(requestGuid))
        .catch(e => this.loggingService.logException(e));
    }
  }

  /**
   * Unsubscribes from notes updates for a specific request and removes the subscription from the tracking set.
   * @param requestGuid The unique identifier of the request to unsubscribe from.
   */
  public unsubscribeToRequestNotes(requestGuid: string) {
    if (this.platformHub) {
      this.platformHub.invoke("Unsubscribe_" + PlatformHub.RequestNotesGroup, requestGuid)
        .then(() => this.subscribedRequestNotesGuids.delete(requestGuid))
        .catch(e => this.loggingService.logException(e));
    }
  }
  /**
   * Resubscribes to all known SignalR groups after a reconnect event.
   */
  private resubscribeAll() {
    this.subscribedRequestStatusGuids.forEach(guid => {
      this.subscribeToRequestStatusGroup(guid);
    });
    this.subscribedRequestEventLogGuids.forEach(guid => {
      this.subscribeToRequestEventLog(guid);
    });
    this.subscribedRequestNotesGuids.forEach(guid => {
      this.subscribeToRequestNotes(guid);
    });
  }

  /**
   * Subscribes to organisation files updates.
   */
  public subscribeOrganisationFiles() {
    if (this.platformHub) {
      this.platformHub.invoke("SubscribeOrganisationFiles").catch((error) => this.loggingService.logException(error));
    }
  }

  /**
   * Unsubscribes from organisation files updates.
   */
  public unsubscribeOrganisationFiles() {
    if (this.platformHub) {
      this.platformHub.invoke("UnsubscribeOrganisationFiles").catch((error) => this.loggingService.logException(error));
    }
  }


  /**
   * Stops the SignalR connection and unsubscribes all event handlers to prevent memory leaks and duplicate events.
   */
  public stop(): void {
    if (this.platformHub) {
      // Unsubscribe all event handlers to prevent memory leaks and duplicate events
      this.platformHub.off('ActorsUpdated');
      this.platformHub.off('EventLogExpanded');
      this.platformHub.off('NoteAdded');
      this.platformHub.off('NoteUpdated');
      this.platformHub.off('DeletedNote');
      this.platformHub.off('RequestUpdated');
      this.platformHub.off('DossierDeleted');
      this.platformHub.off('BuilderRequestUpdated');
      this.platformHub.off('StatusRequestUpdated');
      this.platformHub.stop().catch((error) => this.loggingService.logException(error));
      this.platformHub = null;
    }
    this.starting = false;
  }

  /**
   * Returns the current connection state of the SignalR hub. If not connected, attempts to reconnect after several retries.
   */
  public getConnectionState() {
    if (this.platformHub) {
      return this.platformHub.state;
    } else {
      this.retrys++;
      if (this.retrys > 5) {
        this.retrys = 0;
        this.starting = false;
        this.start();
      }
      return false;
    }
  }
}
